/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  HERCULES — UGC Community Slider JS                             ║
 * ║  Dosya: assets/hercules-ugc-slider.js                           ║
 * ║                                                                  ║
 * ║  Görevler:                                                       ║
 * ║  1. Kartları klonlayarak seamless sonsuz loop oluştur           ║
 * ║  2. CSS animasyon hızını schema ayarından uygula                ║
 * ║  3. Hover'da animasyonu durdur / devam ettir                    ║
 * ║  4. Schema renk & boyut tokenlarını CSS'e aktar                 ║
 * ║  5. Shopify Theme Editor live reload desteği                    ║
 * ╚══════════════════════════════════════════════════════════════════╝
 */

(function () {
  'use strict';

  var SEL_SECTION = '.hugs-section';
  var SEL_OUTER = '.hugs-slider-outer';
  var SEL_TRACK = '.hugs-track';
  var CLS_ANIM = 'hugs-track--animating';
  var CLS_PAUSED = 'hugs-track--paused';

  /* Safari: animation-play-state: paused animasyonu başa sarar.
     Hover’da transform’u dondurup devamde negatif animation-delay kullanıyoruz. */

  function getTrackTranslateX(track) {
    var style = window.getComputedStyle(track);
    var transform = style.transform;
    if (!transform || transform === 'none') return 0;

    if (typeof DOMMatrixReadOnly !== 'undefined') {
      try {
        return new DOMMatrixReadOnly(transform).m41;
      } catch (e) {
        /* fall through */
      }
    }

    var match = transform.match(/matrix(?:3d)?\(([^)]+)\)/);
    if (!match) return 0;
    var parts = match[1].split(',').map(function (s) {
      return parseFloat(s.trim());
    });
    return parts.length === 16 ? parts[12] : parts[4] || 0;
  }

  function getLoopWidth(section) {
    var translate =
      section.style.getPropertyValue('--hugs-translate') ||
      getComputedStyle(section).getPropertyValue('--hugs-translate');
    return Math.abs(parseFloat(translate) || 0);
  }

  function getAnimDuration(track, section) {
    var duration = parseFloat(getComputedStyle(track).animationDuration);
    if (duration && !isNaN(duration)) return duration;
    var speed = section.getAttribute('data-hugs-speed');
    return parseFloat(speed) || 32;
  }

  function pauseTrackAnimation(track, section) {
    if (!track.classList.contains(CLS_ANIM)) return;
    if (track.dataset.hugsPaused === '1') return;

    var x = getTrackTranslateX(track);
    track.dataset.hugsPaused = '1';
    track.dataset.hugsPausedX = String(x);

    track.classList.add(CLS_PAUSED);
    track.style.animation = 'none';
    track.style.transform = 'translateX(' + x + 'px)';
  }

  function resumeTrackAnimation(track, section) {
    if (track.dataset.hugsPaused !== '1') return;

    var x = parseFloat(track.dataset.hugsPausedX || '0');
    var loopWidth = getLoopWidth(section);
    var duration = getAnimDuration(track, section);
    var progress = loopWidth > 0 ? (Math.abs(x) % loopWidth) / loopWidth : 0;
    var elapsed = progress * duration;

    track.style.animation = '';
    track.style.transform = '';
    track.style.animationDelay = '-' + elapsed + 's';
    /* Safari: inline stilleri kaldırdıktan sonra delay’in uygulanması için reflow */
    void track.offsetWidth;
    track.classList.remove(CLS_PAUSED);
    delete track.dataset.hugsPaused;
    delete track.dataset.hugsPausedX;
  }

  function createPauseController(outer, track, section) {
    var count = 0;
    return {
      pause: function () {
        count += 1;
        if (count === 1) pauseTrackAnimation(track, section);
      },
      resume: function () {
        if (count <= 0) return;
        count -= 1;
        if (count === 0) resumeTrackAnimation(track, section);
      },
    };
  }

  /* ─── Tek section başlat ─────────────────────────────────────────── */
  function initSection(section) {
    var sectionId = section.getAttribute('data-section-id');
    var track = section.querySelector(SEL_TRACK);
    var outer = section.querySelector(SEL_OUTER);

    if (!track || !outer) return;

    applyTokens(section);
    var pauseCtrl = createPauseController(outer, track, section);
    buildLoop(track, section);
    setupHoverPause(outer, pauseCtrl);
  }

  /* ─── CSS tokenları uygula ───────────────────────────────────────── */
  function applyTokens(section) {
    var speed = section.getAttribute('data-hugs-speed');
    var gap = section.getAttribute('data-hugs-gap');
    var bg = section.getAttribute('data-hugs-bg');
    var accent = section.getAttribute('data-hugs-accent');
    var pt = section.getAttribute('data-hugs-pt');
    var pb = section.getAttribute('data-hugs-pb');

    if (speed) section.style.setProperty('--hugs-speed', speed + 's');
    if (gap) section.style.setProperty('--hugs-card-gap', gap + 'px');
    if (bg) section.style.setProperty('--hugs-section-bg', bg);
    if (accent) section.style.setProperty('--hugs-section-accent', accent);
    if (pt) section.style.setProperty('--hugs-pt', pt + 'px');
    if (pb) section.style.setProperty('--hugs-pb', pb + 'px');
  }

  /* ─── Seamless loop: kartları klonla ─────────────────────────────── */
  function buildLoop(track, section) {
    var cards = Array.from(track.querySelectorAll('.hugs-card:not(.hugs-card--clone)'));
    if (cards.length === 0) return;

    setupCardHover(track);

    /* reduced-motion kontrolü */
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    /* Kartları klonla — seamless loop için 2 set gerekli */
    cards.forEach(function (card) {
      var clone = card.cloneNode(true);
      clone.classList.add('hugs-card--clone');
      clone.removeAttribute('data-hugs-hover');
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      /* Klondaki linkleri de devre dışı bırak */
      var links = clone.querySelectorAll('a, button');
      links.forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
      track.appendChild(clone);
    });

    setupCardHover(track);

    /* Track genişliğini hesapla, translate değerini ayarla */
    requestAnimationFrame(function () {
      var totalWidth = 0;
      cards.forEach(function (c) {
        totalWidth += c.offsetWidth;
      });
      var gap = parseInt(getComputedStyle(track).gap, 10) || 10;
      if (cards.length > 1) {
        totalWidth += gap * (cards.length - 1);
      }
      section.style.setProperty('--hugs-translate', '-' + totalWidth + 'px');

      /* Animasyonu başlat */
      track.classList.add(CLS_ANIM);
    });
  }

  /* ─── Kart hover — siyah-beyaz → renk (JS class, CSS :hover değil) ─ */
  function setupCardHover(track) {
    track.querySelectorAll('.hugs-card').forEach(function (card) {
      if (card.dataset.hugsHover) return;
      card.dataset.hugsHover = '1';

      card.addEventListener('mouseenter', function () {
        card.classList.add('hugs-card--active');
      });
      card.addEventListener('mouseleave', function () {
        card.classList.remove('hugs-card--active');
      });
      card.addEventListener('focus', function () {
        card.classList.add('hugs-card--active');
      });
      card.addEventListener('blur', function () {
        card.classList.remove('hugs-card--active');
      });
    });
  }

  /* ─── Outer hover — track dur/devam ──────────────────────────────── */
  function setupHoverPause(outer, pauseCtrl) {
    outer.addEventListener('mouseenter', pauseCtrl.pause);
    outer.addEventListener('mouseleave', function () {
      pauseCtrl.resume();
    });
  }

  /* ─── Tüm section'ları başlat ────────────────────────────────────── */
  function init() {
    document.querySelectorAll(SEL_SECTION).forEach(function (section) {
      initSection(section);
    });
  }

  /* ─── Shopify Theme Editor ───────────────────────────────────────── */
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target && e.target.querySelector(SEL_SECTION);
    if (!section) return;

    /* Track'i sıfırla — klon eklenmişse temizle */
    var track = section.querySelector(SEL_TRACK);
    if (track) {
      track.classList.remove(CLS_ANIM, CLS_PAUSED);
      track.style.animation = '';
      track.style.transform = '';
      track.style.animationDelay = '';
      delete track.dataset.hugsPaused;
      delete track.dataset.hugsPausedX;
      track.querySelectorAll('.hugs-card--clone').forEach(function (c) {
        c.remove();
      });
      track.querySelectorAll('.hugs-card').forEach(function (card) {
        card.classList.remove('hugs-card--active');
        delete card.dataset.hugsHover;
      });
    }

    initSection(section);
  });

  document.addEventListener('shopify:section:select', function (e) {
    var section = e.target && e.target.querySelector(SEL_SECTION);
    if (!section) return;
    var track = section.querySelector(SEL_TRACK);
    if (track) pauseTrackAnimation(track, section);
  });

  document.addEventListener('shopify:section:deselect', function (e) {
    var section = e.target && e.target.querySelector(SEL_SECTION);
    if (!section) return;
    var track = section.querySelector(SEL_TRACK);
    if (track) resumeTrackAnimation(track, section);
  });

  /* ─── Başlat ─────────────────────────────────────────────────────── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
